// The record of a finished majlis over HTTPS (MAJLIS-67; P-20..P-23, PROPOSED; D-16, D-45, EM-18, EM-23), its read
// links and its deletion (MAJLIS-66; P-30..P-33, PROPOSED). The credential rides in the Authorization header - NEVER
// in a URL, never in a log line:
//   Bearer <identity id>.<identity secret>                  the device's identity
//   Bearer <identity id>.<identity secret>.<host secret>    while a host link opened after close is being claimed
//   Bearer <identity id>.<identity secret>.v.<read secret>  while a read link (#/v/<secret>) is being opened (P-33)
// After either claim the identity alone is enough: a host by DM-10, a reader by DM-12 while the link stays live.
import type { IdentityProof, SegmentData } from "./types";

export interface RecordSeat {
  id: string;
  name: string;
  kind: "personal" | "shared";
  speak_lang: string;
}

export interface RecordSummary {
  room_id: string;
  created: string;
  started: string | null;
  ended: string | null;
  closed: string;
  minutes: number;
  seats: RecordSeat[];
  langs: string[];
  /** MAJLIS-66 (P-33): live read links of a record this identity hosts; null in `read` */
  links?: number | null;
  /** MAJLIS-145 (P-20, P-38, additive): the room was passive - My majlis shows its Passive tag */
  passive?: boolean;
}

export interface RecordList {
  records: RecordSummary[];
  opened: string | null;
  /** MAJLIS-66 (P-33): "Opened here with a read link" */
  read?: RecordSummary[];
}

export type RecordAccess = "host" | "read";

/** MAJLIS-66 (P-30, P-31): a read link as the host's share sheet lists it - never its secret. */
export interface ReadLinkInfo {
  id: string;
  label: string | null;
  created: string;
  revoked: string | null;
}

export interface ReadLinkCreated {
  link: ReadLinkInfo;
  /** shown ONCE (EM-4): the app shows `<origin>/#/v/<secret>` until the sheet closes, then drops it */
  secret: string;
}

export interface RecordPhrase {
  segment: SegmentData;
  at: string;
  audio_ms: number | null;
  translation: { lang: string; text: string } | null;
  pending: boolean;
}

export interface RecordData {
  room_id: string;
  created: string;
  started: string | null;
  ended: string | null;
  closed: string;
  minutes: number;
  /** the people who sat: one per identity (a rejoin is one person, and the list's seat count) */
  people: number;
  lang: string;
  seats: RecordSeat[];
  phrases: RecordPhrase[];
  /** MAJLIS-66 (P-33): 'host' may share and delete; 'read' reads by a live read link, read only */
  access?: RecordAccess | "admin"; // MAJLIS-ADMIN: the owner's read-only admin view
}

export interface RecordHit {
  segment_id: string;
  original: boolean;
  translation: boolean;
}

export interface RecordSearchResult {
  hits: RecordHit[];
  truncated: boolean;
}

export interface RecordCredential {
  identity: IdentityProof;
  /** the host link's secret (after #/h/), only while a host link is being claimed */
  hostSecret?: string | null;
  /** MAJLIS-66: a read link's secret (after #/v/), only while that link is being opened */
  readSecret?: string | null;
}

export const RECORDS_PATH = "/api/records";
export const recordPath = (roomId: string): string => `${RECORDS_PATH}/${encodeURIComponent(roomId)}`;

/** P-23 / P-33: the one Authorization value of every record request, and of Replay inside a record. */
export function recordAuthorization(c: RecordCredential): string {
  const extra = c.hostSecret ? `.${c.hostSecret}` : c.readSecret ? `.v.${c.readSecret}` : "";
  return `Bearer ${c.identity.id}.${c.identity.secret}${extra}`;
}

/** MAJLIS-66 (P-33, EM-18): the address of a read link - the secret in the FRAGMENT, never the path or query. */
export function readLinkUrl(origin: string, secret: string): string {
  return `${origin}/#/v/${secret}`;
}

/** Every refusal is the server's one 404 (`refused`); anything else that is not a 200 is `failed` (retry by hand). */
export type Fetched<T> = { ok: true; value: T } | { ok: false; why: "refused" | "failed" };

async function call<T>(fetchImpl: typeof fetch, url: string, init: RequestInit): Promise<Fetched<T>> {
  let res: Response;
  try {
    res = await fetchImpl(url, { ...init, cache: "no-store" });
  } catch {
    return { ok: false, why: "failed" };
  }
  if (res.status === 200) {
    try {
      return { ok: true, value: (await res.json()) as T };
    } catch {
      return { ok: false, why: "failed" };
    }
  }
  return { ok: false, why: res.status === 404 ? "refused" : "failed" };
}

/** P-20: this identity's finished majlis, newest first (with a host secret: that closed room is claimed, `opened`). */
export function listRecords(fetchImpl: typeof fetch, c: RecordCredential): Promise<Fetched<RecordList>> {
  return call(fetchImpl, RECORDS_PATH, { headers: { Authorization: recordAuthorization(c) } });
}

/** P-21: one record in the reading language `lang` (a language is not a secret: it is the one query parameter). */
export function readRecord(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
  lang: string,
): Promise<Fetched<RecordData>> {
  return call(fetchImpl, `${recordPath(roomId)}?lang=${encodeURIComponent(lang)}`, {
    headers: { Authorization: recordAuthorization(c) },
  });
}

/** P-22: the words go in the BODY, never the URL (ruling 13). */
export function searchRecord(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
  lang: string,
  q: string,
): Promise<Fetched<RecordSearchResult>> {
  return call(fetchImpl, `${recordPath(roomId)}/search`, {
    method: "POST",
    headers: { Authorization: recordAuthorization(c), "Content-Type": "application/json" },
    body: JSON.stringify({ q, lang }),
  });
}

function withAuth(c: RecordCredential, extra: Record<string, string> = {}): Record<string, string> {
  return { Authorization: recordAuthorization(c), ...extra };
}

/** P-30: the record's read links (host only), newest first, turned-off ones included. */
export function listLinks(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
): Promise<Fetched<{ links: ReadLinkInfo[] }>> {
  return call(fetchImpl, `${recordPath(roomId)}/links`, { headers: withAuth(c) });
}

/** P-30: a new read link; the answer carries its secret, ONCE. The label goes in the body. */
export function createLink(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
  label: string | null,
): Promise<Fetched<ReadLinkCreated>> {
  return call(fetchImpl, `${recordPath(roomId)}/links`, {
    method: "POST",
    headers: withAuth(c, { "Content-Type": "application/json" }),
    body: JSON.stringify(label ? { label } : {}),
  });
}

/** P-31: turn a read link off (host only). */
export function revokeLink(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
  linkId: string,
): Promise<Fetched<{ link: ReadLinkInfo }>> {
  return call(fetchImpl, `${recordPath(roomId)}/links/${encodeURIComponent(linkId)}`, {
    method: "DELETE",
    headers: withAuth(c),
  });
}

/** P-32: delete the record for everyone (host only; refused while the room is open). */
export function deleteRecord(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
): Promise<Fetched<{ room_id: string; objects_pending: boolean }>> {
  return call(fetchImpl, recordPath(roomId), { method: "DELETE", headers: withAuth(c) });
}

// ---------------------------------------------------------------- MAJLIS-153: the record's summary (P-40, D-60)
// The server's answer shapes (server/majlis_server/protocol.py, P.Summary and its parts); tests validate every mocked
// body against docs/protocol.schema.json's http_records.

export interface SummaryPerson {
  name: string;
  /** the person's first seat id in the record: it colours the dot as the record does */
  seat: string;
}

export interface SummaryHeader {
  started: string | null;
  minutes: number;
  people: SummaryPerson[];
  langs: string[];
}

export interface SummaryAction {
  /** a name from the record, or "" when nobody was named */
  who: string;
  what: string;
}

export interface SummaryMoment {
  segment_id: string;
  name: string;
  /** ISO 8601 (UTC) wall time of the phrase's start */
  at: string;
  /** the phrase's STORED words in the reading language, never the model's */
  text: string;
}

export interface SummarySections {
  recap: string[];
  topics: string[];
  decisions: string[];
  actions: SummaryAction[];
  questions: string[];
  moments: SummaryMoment[];
}

export interface Summary {
  room_id: string;
  lang: string;
  model: string;
  created: string;
  record: SummaryHeader;
  sections: SummarySections;
}

/** What one P-40 POST came to, as the summary screen acts on it (UI-C42). */
export type SummaryAnswer =
  | { kind: "ready"; summary: Summary }
  /** 202: POST the same again after this many ms (the Retry-After header; else the body's `retry_after_s`) */
  | { kind: "writing"; retryAfterMs: number }
  /** 409 too_short: its own line, no retry */
  | { kind: "too_short" }
  /** 503 summary_failed - and, by this client's reading, 429 rate_limited (UI-C42 draws no line of its own for it, so
   * it is the failure line with "Try again"), the record's one 404, a 400, a network failure or an unreadable body */
  | { kind: "failed" };

export const summaryPath = (roomId: string, lang: string): string =>
  `${recordPath(roomId)}/summary?lang=${encodeURIComponent(lang)}`;

function retryAfterMs(res: Response, body: unknown): number | null {
  const header = res.headers.get("Retry-After");
  if (header !== null && /^\d+$/.test(header.trim())) return Number(header.trim()) * 1000;
  const s = (body as { retry_after_s?: unknown } | null)?.retry_after_s;
  return typeof s === "number" && Number.isFinite(s) && s >= 0 ? s * 1000 : null;
}

/** P-40: the record's summary in the reading language `lang` (host only; no body). */
export async function requestSummary(
  fetchImpl: typeof fetch,
  c: RecordCredential,
  roomId: string,
  lang: string,
): Promise<SummaryAnswer> {
  let res: Response;
  try {
    res = await fetchImpl(summaryPath(roomId, lang), { method: "POST", headers: withAuth(c), cache: "no-store" });
  } catch {
    return { kind: "failed" };
  }
  const body: unknown = await res.json().catch(() => null);
  if (res.status === 200 && body && typeof body === "object" && "sections" in body) {
    return { kind: "ready", summary: body as Summary };
  }
  if (res.status === 202) {
    const ms = retryAfterMs(res, body);
    return ms === null ? { kind: "failed" } : { kind: "writing", retryAfterMs: ms };
  }
  if (res.status === 409 && (body as { code?: unknown } | null)?.code === "too_short") return { kind: "too_short" };
  return { kind: "failed" };
}
